import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

const inputClass =
  "w-full border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:border-slate-900 focus:outline-none bg-white text-slate-900";

export default function ParticipantLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Admins first -> straight to the admin panel
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (aErr) {
      // Otherwise treat as a participant
      try {
        await api.post("/auth/participant/login", { email, password });
        navigate("/konto");
      } catch (pErr) {
        setError(t.credit.loginError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] text-slate-900 flex flex-col" data-testid="participant-login-page">
      <div className="border-b spec-border bg-white p-6">
        <Link to="/" data-testid="participant-login-logo" className="flex items-center w-fit">
          <Logo markClass="w-6 h-6 text-blue-600" textClass="text-lg" />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm bg-white border spec-border p-8 rounded-sm shadow-sm">
          <h1 className="text-xl font-bold text-slate-950">{t.credit.loginTitle}</h1>
          <p className="text-xs text-slate-500 mt-1 mb-6 font-mono-spec">{t.credit.loginSub}</p>
          <form onSubmit={submit} className="space-y-4" data-testid="participant-login-form">
            <div>
              <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.credit.loginEmail}</label>
              <input
                data-testid="participant-login-email"
                type="email"
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-mono-spec text-slate-700 mb-1">{t.credit.loginPassword}</label>
              <input
                data-testid="participant-login-password"
                type="password"
                className={inputClass}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p className="text-xs text-red-600 font-mono-spec" data-testid="participant-login-error">{error}</p>
            )}
            <div className="text-right">
              <Link to="/passwort-reset" data-testid="participant-forgot-link" className="text-xs font-mono-spec text-blue-700 hover:text-blue-900">
                {t.credit.forgotLink}
              </Link>
            </div>
            <button
              data-testid="participant-login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-mono-spec text-xs font-bold rounded-full uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {t.credit.loginSubmit}
            </button>
          </form>
          <div className="mt-6 pt-5 border-t spec-border text-center">
            <p className="text-xs text-slate-500 mb-3">{t.credit.notRegisteredYet}</p>
            <Link
              to="/people#register"
              data-testid="participant-login-register-link"
              className="inline-flex items-center justify-center w-full py-2.5 border border-blue-600 text-blue-700 hover:bg-blue-50 text-xs font-mono-spec font-bold rounded-full uppercase tracking-wider transition-colors"
            >
              {t.credit.registerCta}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
